// Playback provider abstraction — mirrors the Android app's architecture
// (app/.../playback/PlaybackProvider.kt and PlaybackProviderRegistry):
//
//   PlaybackRequest  -> PlaybackProviderRegistry -> PlaybackSource
//
// A provider only knows how to turn a provider-independent PlaybackRequest into
// a PlaybackSource URL. The UI never constructs provider URLs. Adding a new
// provider means implementing one class and registering it in
// src/playback/registry.js, then optionally setting preferredPlaybackProvider
// in public/config.js.
import { ContentType } from "../api/tmdb";
import { apiUrl, piRelayBase } from "../config";
import { relaySubsUrl, relayVideoUrl, streamtapeFileId } from "./pi-relay";

export const PlaybackStatus = {
  RESOLVED: "RESOLVED",
  UNAVAILABLE: "UNAVAILABLE",
  ERROR: "ERROR",
};

// Should hosted media be fetched through OUR origin rather than the host's CDN?
//
// True on a loopback origin only. The reason is a browser, not the network:
// StreamTape serves its file from *.tapecontent.net with CORS `*`, so the
// bytes are perfectly reachable — but a content blocker (Brave Shields, uBlock
// Origin) cancels the third-party request before it is sent, and the <video>
// then dies with MEDIA_ERR code 4 and a misleading "blocked by CORS policy"
// message, because the cancellation surfaces as a response with no
// Access-Control-Allow-Origin. Relaying hides the host from the blocker: the
// page only ever requests localhost, which no blocker touches, and the server
// (which is not a browser) fetches the CDN with no such restrictions.
//
// Deliberately NOT on in production. The relay moves every video byte through
// the server, which costs nothing on the Mac but is metered bandwidth on
// Netlify — whose credits are already exhausted — so poku-tv keeps the direct
// path. An explicit `?relay=0` wins, which is how a deployed site or a phone
// on mobile data asks for the cheaper direct stream.
function relayThroughOrigin() {
  if (typeof window === "undefined") return false;
  const host = window.location?.hostname || "";
  if (host === "localhost" || host === "127.0.0.1" || host === "::1" || host === "[::1]") {
    try {
      // A stored preference beats the default, so someone on loopback who
      // would rather stream direct (to spare the local server the bandwidth)
      // can say so once.
      if (window.localStorage?.getItem("poku:relay") === "0") return false;
    } catch {
      /* private mode / storage disabled — fall through to the default */
    }
    return true;
  }
  return false;
}

// Everything a provider returns as `url`/`hlsUrl`/`statusUrl` is handed STRAIGHT
// to <video src>, hls.js or an <iframe src> — never through fetch, so the bridge
// in src/native-api.js cannot rewrite it. In the packaged app those paths must
// already be absolute, hence apiUrl() at every construction site below. On the
// web apiBase is "" and these calls are identity functions.
//
// Subtitle URLs arrive from the API the same way and become <track src>, so they
// get the same treatment. Defensive about shape: a track without a url passes
// through untouched rather than becoming "...undefined".
const absSubs = (subs) =>
  Array.isArray(subs)
    ? subs.map((track) => (track && track.url ? { ...track, url: apiUrl(track.url) } : track))
    : null;

// Builds a PlaybackRequest from /watch query parameters.
export function playbackRequestFromParams(params) {
  const type = params.get("type");
  const seasonRaw = params.get("season");
  const episodeRaw = params.get("episode");
  return {
    contentType: type === "episode" ? ContentType.EPISODE : ContentType.MOVIE,
    tmdbId: Number(params.get("id")),
    season: seasonRaw != null ? Number(seasonRaw) : null,
    episode: episodeRaw != null ? Number(episodeRaw) : null,
    title: params.get("title") || "",
    subtitle: params.get("subtitle") || null,
    posterPath: params.get("poster") || null,
    backdropPath: params.get("backdrop") || null,
    resumePositionSeconds: Number(params.get("resume") || 0) || 0,
    // `?lang=ml` is the title's TMDB original language. The player uses it to
    // pick the default AUDIO rendition out of a multi-language master, so a
    // Malayalam film opens in Malayalam rather than in whichever dub the host
    // flagged DEFAULT=YES. PlayerScreen falls back to a metadata lookup when
    // the URL carries none (Continue Watching).
    originalLanguage: params.get("lang") || null,
    // `?link=<id>` pins ONE mapped link instead of taking the first healthy one.
    // Used to play (and therefore verify) a specific link — e.g. a newly added
    // hybrid source that would otherwise sit behind the full package.
    forceLink: params.get("link") || null,
  };
}

// Vidking — the Phase 1 provider. Uses Vidking's normal embedded player; the
// Vidking — REMOVED. vidking.net's nameservers died in September 2026 (SERVFAIL
// at the DNS authority level — Google: "Name servers refused query"), so the
// domain is unreachable from any network. VidLinkProvider below took its slot
// in the registry with the same embed-player architecture.

// (The old VidkingProvider class body was deleted 2026-09-16; see git history
// or the commented block further down if it ever comes back online.)

// VidLink — the replacement embed provider after vidking.net died (Sep 2026).
// Same architecture as the old VidkingProvider: TMDB-keyed embed URLs, served
// through the same-origin /api/vidking/ proxy (which now points at
// vidlink.pro) so ad scripts are stripped, click popups are blocked, and our
// injected PoKu control bar (play/pause, ±10s, volume, fullscreen, gear)
// replaces the provider's own. Their player posts the same PLAYER_EVENT
// envelope (their tmdb field is "mtmdbId" — the proxy shims it to "id"), and
// resume uses the ?startAt= seconds parameter.
export class VidLinkProvider {
  constructor({ baseUrl, proxy } = {}) {
    // `proxy` (config.vidkingProxy) forces the proxied embed on ALL engines
    // when true; otherwise Chromium goes direct (sandbox blocks ads) and
    // Safari/Firefox always proxy — VidLink's "Please Disable Sandbox" gate
    // rejects every sandbox token set there (live-matrix verified 2026-09-20:
    // only the unsandboxed frame plays), so the server-side proxy's ad-strip
    // + window.open stub is the ONLY working ad block on those engines.
    // (?pokuProxy=1/0 overrides everything, for verification.)
    this.proxy = proxy === true;
    this.baseUrl = baseUrl || "https://vidlink.pro";
  }

  // Same-origin proxied URL (/embed/...) or direct, per engine + overrides.
  // Safari/Firefox ALWAYS proxy (their gate rejects every sandbox set, so the
  // proxy's ad-strip + popup-stub is the only working ad block there).
  // Chromium defaults to direct (sandbox blocks ads); config.vidkingProxy or
  // ?pokuProxy=1 forces the proxy there, ?pokuProxy=0 forces direct anywhere.
  embedUrl(path, params) {
    const pref = (() => {
      try {
        return window.__pokuProxyPref;
      } catch {
        return undefined;
      }
    })();
    const isChromium = (() => {
      try {
        return Boolean(window.chrome);
      } catch {
        return false;
      }
    })();
    let useProxy;
    if (pref === true) useProxy = true;
    else if (pref === false) useProxy = false;
    else useProxy = !isChromium || this.proxy === true;
    const base = useProxy ? "/embed" : this.baseUrl;
    return `${base}/${path}?${params.toString()}`;
  }

  get id() {
    return "vidlink";
  }

  supports(request) {
    return (
      request.contentType === ContentType.MOVIE ||
      (request.contentType === ContentType.EPISODE &&
        request.season != null &&
        request.episode != null)
    );
  }

  async resolve(request) {
    if (!this.supports(request)) {
      return {
        provider: this.id,
        contentType: request.contentType,
        tmdbId: request.tmdbId,
        season: request.season,
        episode: request.episode,
        status: PlaybackStatus.UNAVAILABLE,
        errorMessage: "This provider does not support the requested content.",
      };
    }
    const path =
      request.contentType === ContentType.MOVIE
        ? `movie/${request.tmdbId}`
        : `tv/${request.tmdbId}/${request.season}/${request.episode}`;
    // NOTE: limitAds does NOT work — VidLink's server hardcodes the ad flag
    // to false regardless of the URL param (decoded from their SSR payload
    // 2026-09-20). Ad control comes from the sandbox (Chromium) or the
    // server-side proxy (Safari/Firefox) instead. Kept for parity with the
    // TV resolver in case they honor it someday.
    const params = new URLSearchParams({ primaryColor: "f0b44d", autoPlay: "true", limitAds: "true" });
    if (request.resumePositionSeconds >= 5) {
      params.set("startAt", String(Math.floor(request.resumePositionSeconds)));
    }
    return {
      provider: this.id,
      contentType: request.contentType,
      tmdbId: request.tmdbId,
      season: request.season,
      episode: request.episode,
      url: this.embedUrl(path, params),
      status: PlaybackStatus.RESOLVED,
      resumePositionSeconds: request.resumePositionSeconds,
    };
  }
}

// YouTube — admin-mapped official uploads (Shemaroo, Goldmines, Rajshri,
// T-Series…). Plays YouTube's own embed (their sanctioned player). The server
// probes the video via oEmbed before answering: a deleted/private video comes
// back with mapping.dead, the provider reports UNAVAILABLE, and the registry
// falls through to the normal chain — so a dead link never breaks a movie.
export class YouTubeProvider {
  get id() {
    return "youtube";
  }

  supports(request) {
    return request.contentType === ContentType.MOVIE || request.contentType === ContentType.EPISODE;
  }

  async resolve(request) {
    if (!this.supports(request)) {
      return { provider: this.id, status: PlaybackStatus.UNAVAILABLE, errorMessage: "unsupported" };
    }
    const qs = new URLSearchParams({ type: request.contentType === ContentType.EPISODE ? "episode" : "movie", id: String(request.tmdbId) });
    if (request.contentType === ContentType.EPISODE && request.season != null && request.episode != null) {
      qs.set("season", String(request.season));
      qs.set("episode", String(request.episode));
    }
    let mapping = null;
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 3000);
      const r = await fetch(`/api/youtube/get?${qs.toString()}`, { signal: ctrl.signal });
      clearTimeout(t);
      mapping = (await r.json()).mapping;
    } catch {
      mapping = null;
    }
    if (!mapping || mapping.dead === true) {
      return {
        provider: this.id,
        contentType: request.contentType,
        tmdbId: request.tmdbId,
        status: PlaybackStatus.UNAVAILABLE,
        errorMessage: mapping?.dead ? "Mapped video was removed from YouTube" : "No YouTube mapping for this title",
      };
    }
    return {
      provider: this.id,
      contentType: request.contentType,
      tmdbId: request.tmdbId,
      season: request.season,
      episode: request.episode,
      // enablejsapi + origin let the player send postMessage commands
      // (mute/unmute) to the embed, since our corner masks cover YouTube's
      // own volume control.
      url: `https://www.youtube.com/embed/${mapping.videoId}?autoplay=1&rel=0&modestbranding=1&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`,
      videoId: mapping.videoId,
      kind: "youtube",
      sourceLabel: mapping.youtubeAuthor ? `YouTube · ${mapping.youtubeAuthor}` : "YouTube",
      status: PlaybackStatus.RESOLVED,
      resumePositionSeconds: request.resumePositionSeconds,
    };
  }
}

// TorBox — debrid-backed streaming ("seedr-style"). When the admin set a
// TorBox key, mapped titles stream from TorBox's cloud cache: instant for
// already-cached torrents, after a cloud download otherwise. Nothing is
// downloaded on the host machine. The server hands out a signed direct link
// via 302 on /api/torrent/stream, so the player just uses that URL.
export class TorBoxProvider {
  constructor({ enabled = true } = {}) {
    this.enabled = enabled;
  }

  get id() {
    return "torbox";
  }

  supports(request) {
    if (!this.enabled) return false;
    return (
      request.contentType === ContentType.MOVIE ||
      (request.contentType === ContentType.EPISODE &&
        request.season != null &&
        request.episode != null)
    );
  }

  async resolve(request) {
    if (!this.supports(request)) {
      return { provider: this.id, status: PlaybackStatus.UNAVAILABLE, errorMessage: "unsupported" };
    }
    const params = new URLSearchParams({
      type: request.contentType === ContentType.EPISODE ? "episode" : "movie",
      id: String(request.tmdbId),
    });
    if (request.contentType === ContentType.EPISODE) {
      params.set("season", String(request.season));
      params.set("episode", String(request.episode));
    }
    // `?link=` pins one mapped link (see playbackRequestFromParams).
    if (request.forceLink) params.set("link", String(request.forceLink));
    let probe = null;
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 4000);
      const r = await fetch(`/api/torbox/status?${params.toString()}`, { signal: ctrl.signal });
      clearTimeout(t);
      probe = await r.json();
    } catch {
      probe = null;
    }
    if (!probe?.enabled || probe.state === "unavailable") {
      return {
        provider: this.id,
        status: PlaybackStatus.UNAVAILABLE,
        errorMessage: probe?.reason || "TorBox not configured",
      };
    }
    const qs = params.toString();
    if (probe.state === "queued") {
      // Cloud download running; still RESOLVED — TorrentVideo shows progress
      // and the stream endpoint serves nothing until done (then it 302s).
      return {
        provider: this.id,
        contentType: request.contentType,
        tmdbId: request.tmdbId,
        url: `/api/torrent/stream?${qs}`,
        statusUrl: `/api/torbox/status?${qs}`,
        kind: "torbox",
        queued: true,
        queuedProgress: probe.progress || 0,
        status: PlaybackStatus.RESOLVED,
        resumePositionSeconds: request.resumePositionSeconds,
      };
    }
    if (probe.state === "error") {
      return { provider: this.id, status: PlaybackStatus.UNAVAILABLE, errorMessage: probe.error || "TorBox error" };
    }
    // ready
    return {
      provider: this.id,
      contentType: request.contentType,
      tmdbId: request.tmdbId,
      season: request.season,
      episode: request.episode,
      url: `/api/torrent/stream?${qs}`,
      statusUrl: `/api/torbox/status?${qs}`,
      kind: "torbox",
      status: PlaybackStatus.RESOLVED,
      resumePositionSeconds: request.resumePositionSeconds,
    };
  }
}

// Torrent — admin-mapped torrents, streamed server-side. When a title has a
// torrent mapped, this provider wins; otherwise it reports UNAVAILABLE so the
// registry falls through to Vidking. resolve() only checks that a mapping
// exists (fast); the actual stream URL points at /api/torrent/stream which
// serves the biggest video file from the swarm with Range support.
export class TorrentProvider {
  constructor({ enabled = true } = {}) {
    this.enabled = enabled;
  }

  get id() {
    return "torrent";
  }

  supports(request) {
    if (!this.enabled) return false;
    return (
      request.contentType === ContentType.MOVIE ||
      (request.contentType === ContentType.EPISODE &&
        request.season != null &&
        request.episode != null)
    );
  }

  async resolve(request) {
    if (!this.supports(request)) {
      return { provider: this.id, status: PlaybackStatus.UNAVAILABLE, errorMessage: "unsupported" };
    }
    const params = new URLSearchParams({
      type: request.contentType === ContentType.EPISODE ? "episode" : "movie",
      id: String(request.tmdbId),
    });
    if (request.contentType === ContentType.EPISODE) {
      params.set("season", String(request.season));
      params.set("episode", String(request.episode));
    }
    // Probe whether a mapping exists (server answers instantly from its map).
    let mapped = false;
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 3000);
      const r = await fetch(`/api/torrent/status?${params.toString()}`, { signal: ctrl.signal });
      clearTimeout(t);
      mapped = Boolean((await r.json()).mapped);
    } catch {
      mapped = false;
    }
    if (!mapped) {
      return {
        provider: this.id,
        contentType: request.contentType,
        tmdbId: request.tmdbId,
        status: PlaybackStatus.UNAVAILABLE,
        errorMessage: "No torrent mapped for this title",
      };
    }
    const qs = params.toString();
    return {
      provider: this.id,
      contentType: request.contentType,
      tmdbId: request.tmdbId,
      season: request.season,
      episode: request.episode,
      url: apiUrl(`/api/torrent/stream?${qs}`),
      statusUrl: apiUrl(`/api/torrent/status?${qs}`),
      // Path-based HLS URL (master playlist); only valid once the remux is
      // ready, which TorrentVideo discovers via statusUrl. The playlists this
      // resolves to carry RELATIVE child paths, which the player resolves
      // against this absolute URL — no per-segment rewriting needed.
      hlsUrl: apiUrl(`/api/torrent/hls/${request.contentType === ContentType.EPISODE ? `tv_${request.tmdbId}_${request.season}_${request.episode}` : `movie_${request.tmdbId}`}/master.m3u8`),
      kind: "torrent",
      status: PlaybackStatus.RESOLVED,
      resumePositionSeconds: request.resumePositionSeconds,
    };
  }
}

// Hosted file — admin-mapped LuluStream links (structured for more hosts).
// The admin pastes a hosted link per TMDB title; the server extracts the
// host's direct HLS stream at resolve time and relays it through
// /api/hosted/media (their CDN requires a luluvdo Referer + browser UA the
// browser cannot send from our origin). When extraction fails but the file
// exists, the source falls back to the host's own iframe embed. No mapping or
// a dead file reports UNAVAILABLE and the registry falls through — hosted
// files are one source in the chain, never a single point of failure.
export class HostedFileProvider {
  get id() {
    return "hosted";
  }

  supports(request) {
    return (
      request.contentType === ContentType.MOVIE ||
      (request.contentType === ContentType.EPISODE &&
        request.season != null &&
        request.episode != null)
    );
  }

  async resolve(request) {
    if (!this.supports(request)) {
      return { provider: this.id, status: PlaybackStatus.UNAVAILABLE, errorMessage: "unsupported" };
    }
    const params = new URLSearchParams({
      type: request.contentType === ContentType.EPISODE ? "episode" : "movie",
      id: String(request.tmdbId),
    });
    if (request.contentType === ContentType.EPISODE) {
      params.set("season", String(request.season));
      params.set("episode", String(request.episode));
    }
    // `?link=` pins one mapped link (see playbackRequestFromParams).
    if (request.forceLink) params.set("link", String(request.forceLink));
    // `?relay=1` asks the server for a SAME-ORIGIN media URL
    // (/api/hosted/media?u=…) instead of the raw CDN one, so the browser only
    // ever talks to us. That is what makes a StreamTape file play inside Brave
    // or behind uBlock: a content blocker lets a localhost request through,
    // where the direct tapecontent.net request is cancelled outright
    // (net::ERR_BLOCKED_BY_CLIENT, then MEDIA_ERR code 4) and playback dies.
    //
    // Loopback origins only. Relaying moves every video byte through the
    // server: free on the Mac, but metered on Netlify, whose credits are
    // already exhausted — so production keeps the direct path and its zero
    // relay cost. `?relay=0` forces direct playback anywhere.
    if (relayThroughOrigin()) params.set("relay", "1");
    // `?fresh=1` tells the server to mint a NEW StreamTape token instead of
    // serving the cached one. PlayerScreen sends it on the FIRST runtime death
    // of a hosted source, before it gives up on the provider: a token that has
    // quietly expired is by far the likeliest cause, and re-minting brings the
    // title back on the admin's own copy rather than on a third-party embed.
    if (request.freshToken) params.set("fresh", "1");
    let probe = null;
    try {
      const ctrl = new AbortController();
      // 14s, not 5s: a hybrid link needs the host to mint a fresh signed video
      // URL, and the first mint carries their anti-bot wait. PlayerScreen warms
      // this on mount so playback usually finds it cached and returns at once.
      const t = setTimeout(() => ctrl.abort(), 14000);
      const r = await fetch(apiUrl(`/api/hosted/resolve?${params.toString()}`), { signal: ctrl.signal });
      clearTimeout(t);
      probe = await r.json();
    } catch {
      probe = null;
    }
    if (!probe || probe.kind === "none") {
      return {
        provider: this.id,
        contentType: request.contentType,
        tmdbId: request.tmdbId,
        status: PlaybackStatus.UNAVAILABLE,
        errorMessage: probe?.error || "No hosted file mapped for this title",
      };
    }
    if (probe.kind === "hls") {
      // Direct stream — plays in our own player (TorrentVideo), full custom UI.
      return {
        provider: this.id,
        contentType: request.contentType,
        tmdbId: request.tmdbId,
        season: request.season,
        episode: request.episode,
        url: apiUrl(probe.hlsUrl),
        hlsUrl: apiUrl(probe.hlsUrl),
        kind: "hosted",
        status: PlaybackStatus.RESOLVED,
        resumePositionSeconds: request.resumePositionSeconds,
        subs: absSubs(probe.subs),
      };
    }
    if (probe.kind === "hybrid") {
      // Host video + our own audio renditions and subtitles (see web/hybrid.js).
      // Plays in our own player, so the host gets no say in the UI, and the
      // host's flattened audio track is muted while ours carries the sound.
      return {
        provider: this.id,
        contentType: request.contentType,
        tmdbId: request.tmdbId,
        season: request.season,
        episode: request.episode,
        url: probe.videoUrl,
        videoUrl: probe.videoUrl,
        videoHost: probe.videoHost || probe.host || null,
        audioTracks: Array.isArray(probe.audioTracks) ? probe.audioTracks : [],
        audio: probe.audio || null,
        // No package audio: the host's own track is the sound (subtitles-only
        // multi), so the player keeps the host video audible instead of muting
        // it for an audio element that does not exist.
        hostAudio: probe.hostAudio === true,
        offsetMs: Number(probe.offsetMs || 0),
        kind: "hybrid",
        status: PlaybackStatus.RESOLVED,
        resumePositionSeconds: request.resumePositionSeconds,
        subs: probe.subs || null,
      };
    }
    // Progressive hosted files use the native video player.
    if (probe.kind === "mp4") {
      const directUrl = apiUrl(probe.mp4Url);
      // The Pi relay, when one is configured (config.piRelayBase; empty = off).
      // It fronts the SAME StreamTape file, so seeking is unchanged: the relay
      // forwards Range through and returns 206 with content-range (see
      // web/poku-stream-relay.mjs). What it adds is subtitles — several mapped
      // titles have a track in the Pi's store and none in the public one — and
      // a URL that does not expire with the signature.
      //
      // The relay is used only when the file id is actually known; otherwise
      // this falls through to the signed CDN URL, which is the behaviour with
      // no relay configured at all. StreamTape's own /e/ embed is not used on
      // either path.
      const fileId = probe.host === "streamtape" || !probe.host ? streamtapeFileId(probe) : null;
      const relayUrl = relayVideoUrl(piRelayBase, fileId);
      // When the server already returned a relay URL in mp4Url (POKU_RELAY_BASE
      // was set on the backend), streamtapeFileId() cannot extract the id from
      // the relay path and relayUrl is null. Still populate relayBase so
      // TorrentVideo can load subtitles from the relay's subtitle store.
      const effectiveRelayBase = relayUrl
        ? piRelayBase
        : (piRelayBase && directUrl.startsWith(piRelayBase) ? piRelayBase : null);

      return {
        provider: this.id,
        contentType: request.contentType,
        tmdbId: request.tmdbId,
        season: request.season,
        episode: request.episode,
        url: relayUrl || directUrl,
        hlsUrl: null,
        host: probe.host || "streamtape",
        audio: probe.audio || null,
        relayUrl,
        fileId: fileId || null,
        relaySubsUrl: effectiveRelayBase ? relaySubsUrl(effectiveRelayBase, request) : null,
        relayBase: effectiveRelayBase,
        kind: "hosted-mp4",
        status: PlaybackStatus.RESOLVED,
        resumePositionSeconds: request.resumePositionSeconds,
        subs: absSubs(probe.subs),
      };
    }
    // kind === "embed" — the host's page is playable (Playmate embed, or a
    // lulustream page when HLS extraction missed); render it in our own
    // iframe so users never see Vidking. Return it as a direct URL the
    // PlayerScreen iframe renderer can hand to <iframe>.
    if (probe.kind === "embed") {
      return {
        provider: this.id,
        contentType: request.contentType,
        tmdbId: request.tmdbId,
        season: request.season,
        episode: request.episode,
        url: apiUrl(probe.embedUrl),
        embedUrl: apiUrl(probe.embedUrl),
        host: probe.host || null,
        audio: probe.audio || null,
        subs: absSubs(probe.subs),
        kind: "hosted-embed",
        status: PlaybackStatus.RESOLVED,
        resumePositionSeconds: request.resumePositionSeconds,
      };
    }
    // Terminal fall-through. Without this, any probe whose shape is not one of
    // none/hls/hybrid/embed — an API error body like {"error":"id required"},
    // or a new kind added upstream — made resolve() return undefined. The
    // registry then read `.status` off undefined, threw, and took the ENTIRE
    // chain down with it, so the reserve tier was never even consulted and the
    // user got "Playback unavailable" (observed live 2026-09-24). A provider
    // must always answer: treat anything unrecognised as a decline.
    return {
      provider: this.id,
      contentType: request.contentType,
      tmdbId: request.tmdbId,
      status: PlaybackStatus.UNAVAILABLE,
      errorMessage: probe?.error || "Unrecognised hosted resolve response",
    };
  }
}

// SuperEmbed (multiembed.mov) — the registry's last resort. Keyed by TMDB id
// like Vidking, so it needs no admin mapping: when Vidking's copy of a title
// is gone, this gives playback one more chance instead of an error screen.
// The embed itself is cross-origin (its hosts rotate and front players behind
// interstitials we can't strip), so it loads DIRECTLY — no proxy — while the
// popup guard on the window-level still applies where the browser permits.
// Availability is probed server-side (/api/vidking/superembed-check) so an
// empty catalog answer is UNAVAILABLE and the chain ends cleanly.
export class SuperEmbedProvider {
  get id() {
    return "superembed";
  }

  supports(request) {
    return (
      request.contentType === ContentType.MOVIE ||
      (request.contentType === ContentType.EPISODE &&
        request.season != null &&
        request.episode != null)
    );
  }

  async resolve(request) {
    if (!this.supports(request)) {
      return { provider: this.id, status: PlaybackStatus.UNAVAILABLE, errorMessage: "unsupported" };
    }
    const params = new URLSearchParams({
      type: request.contentType === ContentType.EPISODE ? "tv" : "movie",
      id: String(request.tmdbId),
    });
    if (request.contentType === ContentType.EPISODE) {
      params.set("season", String(request.season));
      params.set("episode", String(request.episode));
    }
    // `?link=` pins one mapped link (see playbackRequestFromParams).
    if (request.forceLink) params.set("link", String(request.forceLink));
    let probe = null;
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 5000);
      const r = await fetch(`/api/vidking/superembed-check?${params.toString()}`, { signal: ctrl.signal });
      clearTimeout(t);
      probe = await r.json();
    } catch {
      probe = null;
    }
    // Transient (probe network trouble) still proceeds — the embed may work.
    if (probe && probe.available === false && probe.dead) {
      return {
        provider: this.id,
        contentType: request.contentType,
        tmdbId: request.tmdbId,
        status: PlaybackStatus.UNAVAILABLE,
        errorMessage: "No SuperEmbed source for this title",
      };
    }
    const embed = new URLSearchParams({ video_id: String(request.tmdbId), tmdb: "1" });
    const path =
      request.contentType === ContentType.MOVIE
        ? `?${embed.toString()}`
        : `?${(() => {
            embed.set("s", String(request.season));
            embed.set("e", String(request.episode));
            return embed.toString();
          })()}`;
    return {
      provider: this.id,
      contentType: request.contentType,
      tmdbId: request.tmdbId,
      season: request.season,
      episode: request.episode,
      // multiembed.mov ships an intellipopup-style POPUNDER script (decoded:
      // base64 CDN list, fires on their own schedule) + histats tracking.
      // NEVER load it direct — always through the same-origin proxy, which
      // strips the ad script and stubs window.open inside the page. This is
      // the fallback path, so ads here previously leaked on every browser
      // where the VidLink chain stalled. Route shape: /embed/superembed/…
      // (the bare /embed/?… prefix collides with VidLink's /embed/{movie,tv}/…
      // path validator and 400s).
      url: apiUrl(`/embed/superembed/?${embed.toString()}`),
      kind: "superembed",
      status: PlaybackStatus.RESOLVED,
      resumePositionSeconds: request.resumePositionSeconds,
    };
  }
}

// Reserve embeds — the six third-party hosts the admin chose as the backup
// tier (vidbolt, vidsrc, vidnest, vidcore, cinesrc, streamrip).
//
// These are embedded DIRECTLY, cross-origin, exactly like the host embeds
// PlayerScreen already handles (see HostedEmbedPlayer): the frame is the
// host's own page on the host's own origin. That is what makes them play —
// their players and CDNs expect their own origin (the same lesson recorded in
// HostedEmbedPlayer, and confirmed live here: served through our same-origin
// proxy, cinesrc's Next.js runtime requests /_next/static/chunks/* unprefixed
// and 404s, and vidbolt/vidnest never finish booting).
//
// CONSEQUENCE, stated plainly: a cross-origin frame cannot be modified, so
// these hosts' own ads come through. The same-origin /embed/reserve/<key>/
// proxy in playback-proxy.js (with its ad stripping + popup guard + the
// allowlist in web/reserve-embeds.js) is kept intact and tested, so switching
// a host back to the proxied path is a change to this table, not a rewrite.
//
// Each entry is a separate provider (id "reserve-<key>") rather than one
// provider holding a list, so a host that fails at runtime walks to the NEXT
// host instead of dropping the whole tier.
//
// A host with no TV route sets `tv: null` and is skipped for episodes rather
// than serving an error.
//
// The routes below are each host's PUBLIC embed routes and mirror the ones in
// web/reserve-embeds.js (which the dormant proxy uses).
// CineSrc leads: the admin's call ("add this on first"), and it is the one host
// with real documentation — https://cinesrc.st/docs — giving TMDB-keyed embed
// routes, a documented customization query set, a documented postMessage event
// stream (cinesrc:ready / play / pause / timeupdate{currentTime,duration} /
// seeking / seeked / ended / volumechange / ratechange) and a command API.
//
// NOTE the TV shape: /embed/tv/{id}?s={season}&e={episode} — season and episode
// are QUERY parameters here, not path segments. Probing path-segment shapes
// (/embed/tv/1396/1/1) 404s, which is what made this host look movie-only.
export const RESERVE_EMBED_HOSTS = [
  {
    key: "cinesrc",
    label: "CineSrc",
    origin: "https://cinesrc.st",
    movie: (id) => `/embed/movie/${id}`,
    tv: (id, s, e) => `/embed/tv/${id}?s=${s}&e=${e}`,
    // Documented customization params. color must be URL-encoded hex (%23 for
    // #) — that is CineSrc's convention, so PoKu's accent is passed as
    // %23f0b44d. autoplay is already their default; set explicitly anyway.
    params: { autoplay: "true", color: "#f0b44d" },
    // Render in a sandbox that withholds top-level navigation and popups.
    // Verified 2026-09-24 with a single sandboxed frame: cinesrc:ready,
    // cinesrc:sourceused, cinesrc:play and an advancing timeupdate all still
    // arrive, so playback is unaffected. Only hosts VERIFIED under a sandbox
    // get this flag — a host that silently refuses to boot inside one shows a
    // blank frame, which is worse than the hazard being guarded against.
    // (An earlier read of "sandbox breaks cinesrc" was my own test harness
    // piling up 1045 frames; re-tested cleanly at one frame, it plays.)
    sandbox: true,
    // Documented start-time param (alias: time) — used for resume.
    resumeParam: "t",
  },
  {
    key: "vidbolt",
    label: "VidBolt",
    // Documented at https://vidbolt.pro/#docs ("Four steps. That's the whole
    // API.") — the most complete docs of the six, and the only host that also
    // documents a COMMAND api (play / pause / seek{time} / volume{volume},
    // posted to the frame, which needs id="vidbolt-player" on it).
    //
    // ROUTES: the docs give the SHORT form (/movie/{tmdb_id}, /tv/{tmdb_id}/
    // {season}/{episode}) with no /embed/ prefix, which is what is used here.
    // Both shapes were measured working 2026-09-24 — a side-by-side mount of
    // /movie/550 and /embed/movie/550 produced identical event streams (111
    // events each, ready + play + durationchange + timeupdate advancing) — so
    // this is a deliberate choice for the documented form, not a repair.
    // (The server answers 200 with the same SPA shell for ANY path, so status
    // codes alone cannot distinguish the shapes; only the browser can.)
    //
    // PLAYBACK VERIFIED 2026-09-24: posts the documented events with an
    // advancing clock, so this host genuinely plays. Their postMessage shape is
    // FLAT and differs from vidnest's nested one — { type: 'timeupdate', time }
    // and { type: 'durationchange', duration } — with events ready / play /
    // pause / timeupdate / durationchange / ended, all from origin
    // https://vidbolt.pro.
    //
    // Documented params: theme (hex WITHOUT #, or a CSS name), autoPlay (note
    // the capital P — value 'true'), startAt (start seconds — used for resume),
    // audio (preferred audio track: 'en', 'hindi', or a track name — wired to
    // the request's originalLanguage), and title / poster / hideServer /
    // fullscreenButton / idType, left unset: hideServer and fullscreenButton
    // would remove the viewer's escape hatches, and title/poster only mirror
    // overlays PoKu already provides.
    origin: "https://vidbolt.pro",
    movie: (id) => `/movie/${id}`,
    tv: (id, s, e) => `/tv/${id}/${s}/${e}`,
    params: { autoPlay: "true", theme: "f0b44d" },
    resumeParam: "startAt",
    audioParam: "audio",
  },
  {
    key: "vidsrc",
    label: "VidSrc",
    // Documented at https://vidsrc.sbs/. Routes confirmed: /embed/movie/{tmdb_id}
    // and /embed/tv/{tmdb_id}/{season}/{episode}.
    //
    // Documented params, all wired: autoplay=1 (note their value is 1, not
    // "true"), color (hex WITHOUT #, like vidcore/theme and unlike cinesrc's
    // %23 form), t (start seconds — used for resume), sub (ISO 639-1 subtitle
    // preselection). Not used: controls=0, which hides their native controls.
    //
    // This is the host the earlier analysis found carrying an llvpn.com
    // popunder on the proxied path, and Cloudflare challenge-platform scripts
    // that made the same-origin proxy return a dead 283-byte page. Loaded
    // DIRECTLY from their own origin (as here) it is not proxied at all, so
    // neither of those applies — but neither is anything stripped, so their
    // own ads come through.
    //
    // MEASURED 2026-09-24. What this host actually is: a WordPress front-end
    // that EMBEDS ANOTHER PROVIDER. Its own network log shows
    //   web.nxsha.app/embed/movie/550?server=AwsPly-[Multi-Lang]  (Document)
    // i.e. the real player is a nested cross-origin frame on nxsha.app, with
    // vidsrc.sbs only supplying the server picker. Its page title claims
    // "API no ADS", which the same log contradicts:
    //   luugy.com/5/11807730/?...&js_build=iclick-v1.1913.0&dmn=llvpn.com
    // — the iclick click-tracker whose dmn=llvpn.com parameter is the popunder
    // host found in the original analysis, plus an admin-ajax.php call for its
    // own picker. Playback sits behind a "Please Confirm To Continue / OK"
    // click-gate which is what arms that tracker, and the modal renders inside
    // a nested frame, so it cannot be dismissed programmatically — playback was
    // therefore NOT verified here. Emits no postMessage events at all, matching
    // its docs, which document no event API (unlike cinesrc/vidnest/vidcore).
    // The nested player means the interesting target may be nxsha.app directly,
    // not vidsrc.sbs — untested, and not one of the six.
    origin: "https://vidsrc.sbs",
    movie: (id) => `/embed/movie/${id}`,
    tv: (id, s, e) => `/embed/tv/${id}/${s}/${e}`,
    params: { autoplay: "1", color: "f0b44d" },
    resumeParam: "t",
    subParam: "sub",
  },
  {
    key: "vidnest",
    label: "VidNest",
    // Documented at https://vidnest.fun/ (API Documentation + Progress Tracking
    // Docs). Routes confirmed: /movie/{tmdb_id}, /tv/{tmdb_id}/{season}/{episode}
    // — no /embed/ prefix. Also documented, not used here: /anime/ and
    // /animepahe/, which key on ANILIST ids (flat episode numbering, plus a
    // sub/dub path segment), so they need an AniList↔TMDB mapping PoKu has no
    // source for yet.
    //
    // startAt/progress (documented resume param: ?startAt=N or ?progress=N) —
    // wired through resumeParam, so Continue Watching resumes inside the
    // player instead of restarting the film.
    //
    // IMPORTANT: their postMessage protocol is the SAME dialect VidLink used —
    // a PLAYER_EVENT message whose data is { event: play|pause|ended|seeked|
    // timeupdate, currentTime, duration } — alongside MEDIA_DATA for metadata,
    // all from origin https://vidnest.fun. The parser this app already ships
    // for VidLink (src/playback/playerEvents.js, isVidkingOrigin/parsePlayerEvent)
    // models exactly that shape, so a stall watchdog or progress save for this
    // host is a reuse of existing code, not new code. Their documented server
    // param picks the source: lambda, primesrc, sigma, alfa, beta, gama,
    // catflix, hexa, delta (left unset — a forced server can be region-wrong);
    // they also document control-hiding params, unused here.
    // Playback VERIFIED 2026-09-24 in the browser: the frame emits PLAYER_EVENT
    // (play, seeked) and periodic MEDIA_DATA with an advancing clock, i.e. this
    // host genuinely plays — unlike streamrip (ad redirect) and vidcore
    // (ad/CAPTCHA wall).
    //
    // NO sandbox flag, deliberately and by measurement: a sandboxed probe frame
    // emitted NOTHING at all (no MEDIA_DATA, no PLAYER_EVENT) and painted an
    // empty box, where the unsandboxed frame played within ~20s. Their player
    // evidently won't boot sandboxed — the same class of gate the codebase
    // records for VidLink and Playmate. Not worth re-testing without
    // allow="autoplay" on the probe, but until someone does, the flag stays off
    // rather than being assumed to work.
    origin: "https://vidnest.fun",
    movie: (id) => `/movie/${id}`,
    tv: (id, s, e) => `/tv/${id}/${s}/${e}`,
    resumeParam: "progress",
  },
  {
    key: "vidcore",
    label: "VidCore",
    // Documented at https://www.vidcore.org/#documentation. The docs print the
    // bare host (vidcore.org/embed/movie/{tmdb_id}) but that 307-redirects to
    // www — verified 2026-09-24 — so www is used here to keep the frame on a
    // direct 200 instead of paying a redirect hop.
    origin: "https://www.vidcore.org",
    movie: (id) => `/embed/movie/${id}`,
    tv: (id, s, e) => `/embed/tv/${id}/${s}/${e}`,
    // Documented optional params. `theme` takes hex WITHOUT the leading '#'
    // (docs example: ?theme=ff0000), unlike CineSrc's URL-encoded %23 form.
    // autoplay is documented; other available params (title, poster, startAt,
    // disableInfo, disableControls, showRelated, lang) are left unset.
    // Their documented postMessage events are vidcore:play / vidcore:pause /
    // vidcore:ended — worth knowing if a stall watchdog is ever armed here.
    // Documented failure modes: 404 NOT_FOUND (id not in their index),
    // 429 RATE_LIMIT, 500 GATEWAY_ERROR (upstream scraper unreachable).
    //
    // WARNING — measured 2026-09-24, three loads, none reached playback:
    //   sandboxed frame   -> "HURRY UP! / Claim your bonus / GET BONUS"
    //                        affiliate countdown ad
    //   unsandboxed frame -> explicit ADULT dating creative over the frame
    //   top-level tab     -> ad interstitial, then a "select all images with
    //                        motorcycles" bot CAPTCHA over a black player
    // The page's own title is "Fight Club — Player", so the routes and ids are
    // right — what is gated is playback, by an ad/CAPTCHA wall a cross-origin
    // frame cannot strip (and the same-origin proxy cannot serve: the player is
    // client-rendered, so it 404s its own chunks there — see the tier note
    // above). The creatives vary per load and by IP, so treat the exact ads as
    // samples but the wall as consistent. DO NOT ship this in front of users
    // without deciding about that adult creative first; removing the host from
    // config.reserveEmbeds is the one-line fix.
    params: { autoplay: "true", theme: "f0b44d" },
    // Documented start-time param (docs also list `startAt`).
    resumeParam: "startAt",
  },
  {
    key: "streamrip",
    label: "StreamRip",
    origin: "https://streamrip.fun",
    // Documented at https://streamrip.fun/api-docs#how-to. Note the routes
    // carry NO /embed/ prefix (unlike cinesrc/vidbolt/vidsrc/vidcore) and the
    // docs give the same two shapes used here: /movie/{tmdb_id} and
    // /tv/{tmdb_id}/{season}/{episode}.
    movie: (id) => `/movie/${id}`,
    tv: (id, s, e) => `/tv/${id}/${s}/${e}`,
  },
];

export class ReserveEmbedProvider {
  constructor({ key, label, origin, movie, tv, params, sandbox, resumeParam, subParam, audioParam } = {}) {
    this.key = key;
    this.label = label || key;
    this.origin = origin;
    this.moviePath = movie || null;
    this.tvPath = tv || null;
    this.params = params || null;
    this.sandbox = sandbox === true;
    // Name of this host's documented "start playback at N seconds" query
    // param, if it has one (cinesrc: t, vidnest: progress, vidcore: startAt,
    // vidsrc: t). Set per host rather than guessed, so a host that doesn't
    // document one never gets an unknown param appended.
    this.resumeParam = resumeParam || null;
    // Name of this host's documented subtitle-preselection param, if any
    // (vidsrc: sub).
    this.subParam = subParam || null;
    // Name of this host's documented audio-track preference param, if any
    // (vidbolt: audio). Fed from the request's originalLanguage, so a film
    // plays on its own audio instead of the first dub in the list.
    this.audioParam = audioParam || null;
    // Last resort. The registry consults `deferred` providers only after every
    // other provider has declined, because these resolve a URL instantly
    // without probing anything and would otherwise win the race by being
    // fastest at saying nothing (see PlaybackProviderRegistry.resolve).
    this.deferred = true;
  }

  get id() {
    return `reserve-${this.key}`;
  }

  supports(request) {
    const isEpisode = request.contentType === ContentType.EPISODE;
    if (isEpisode) {
      return Boolean(this.tvPath) && request.season != null && request.episode != null;
    }
    return request.contentType === ContentType.MOVIE && Boolean(this.moviePath);
  }

  async resolve(request) {
    if (!this.supports(request)) {
      return {
        provider: this.id,
        contentType: request.contentType,
        tmdbId: request.tmdbId,
        status: PlaybackStatus.UNAVAILABLE,
        errorMessage: "This provider does not support the requested content.",
      };
    }
    const path =
      request.contentType === ContentType.EPISODE
        ? this.tvPath(request.tmdbId, request.season, request.episode)
        : this.moviePath(request.tmdbId);
    // Build through URL so a host whose route already carries a query string
    // (cinesrc's /embed/tv/{id}?s=&e=) gets its params merged rather than a
    // second, invalid "?".
    const target = new URL(this.origin);
    const built = new URL(path, this.origin);
    target.pathname = built.pathname;
    built.searchParams.forEach((value, name) => target.searchParams.set(name, value));
    for (const [name, value] of Object.entries(this.params || {})) {
      target.searchParams.set(name, String(value));
    }
    // Resume: hand the player the position PoKu already knows, so Continue
    // Watching continues inside the host's player rather than restarting the
    // title at 0:00. Only when the host documents such a param AND there is a
    // real position — a 0 would make cinesrc show its "Continue / Restart"
    // prompt on every fresh start.
    const resume = Number(request.resumePositionSeconds || 0);
    if (this.resumeParam && resume > 0) {
      target.searchParams.set(this.resumeParam, String(Math.floor(resume)));
    }
    // Subtitle preselection, but ONLY for a 2-letter ISO 639-1 code — what
    // these hosts document. Anything else (a label, a full locale) is dropped
    // rather than sent as a value the player can't match.
    const sub = request.subtitle ? String(request.subtitle).toLowerCase() : "";
    if (this.subParam && /^[a-z]{2}$/.test(sub)) {
      target.searchParams.set(this.subParam, sub);
    }
    // Audio-track preference, same 2-letter ISO 639-1 guard. TMDB's
    // originalLanguage is exactly the code these hosts document, so this is
    // the film's own audio rather than whichever dub a title happens to list
    // first. (vidbolt's docs also accept a track NAME, but nothing in the
    // request carries one, so only codes are ever sent.)
    const audio = request.originalLanguage ? String(request.originalLanguage).toLowerCase() : "";
    if (this.audioParam && /^[a-z]{2}$/.test(audio)) {
      target.searchParams.set(this.audioParam, audio);
    }
    return {
      provider: this.id,
      contentType: request.contentType,
      tmdbId: request.tmdbId,
      season: request.season,
      episode: request.episode,
      // Direct, cross-origin: the host's own page on the host's own origin.
      // See the table comment above for why this is not the proxied path.
      url: target.toString(),
      kind: "reserve",
      sourceLabel: this.label,
      // Passed to PlayerScreen so it can sandbox this frame (see the flag's
      // note in the host table). Left unset for hosts that have not been
      // verified to play inside a sandbox.
      sandbox: this.sandbox === true,
      status: PlaybackStatus.RESOLVED,
      resumePositionSeconds: request.resumePositionSeconds,
    };
  }
}

// Picks the preferred provider that supports the request, falling back to any
// registered provider that does (mirrors the Android PlaybackProviderRegistry).
// A provider whose resolve() reports UNAVAILABLE is skipped in favor of the
// next one — that is how torrent-mapped titles stream from the swarm while
// everything else keeps using Vidking.
export class PlaybackProviderRegistry {
  constructor(providers, preferredProvider = "vidlink") {
    this.providers = providers;
    this.providersById = new Map(providers.map((p) => [p.id, p]));
    this.preferredProvider = preferredProvider;
  }

  async resolve(request, { exclude = [], fresh = false } = {}) {
    // `fresh` reaches the providers through the request, because they take only
    // `request` today and threading a second argument through every provider is
    // noise. It is deliberately NOT on the memoised request object PlayerScreen
    // holds: changing that would re-fire the resolve effect and restart
    // playback. One shallow copy, used only for this resolve call.
    const req = fresh ? { ...request, freshToken: true } : request;
    // `exclude` lets the UI drop a provider that failed at RUNTIME (e.g. the
    // Vidking player reported a fatal source error after resolving fine) and
    // re-resolve on the remaining ones without re-trying the dead one.
    const skipped = new Set(exclude);
    const eligible = this.providers.filter(
      (p) => !skipped.has(p.id) && p.supports(req),
    );
    if (!eligible.length) throw new Error(`No playback provider supports ${request.contentType}`);

    // Preferred provider first, exactly as configured.
    const preferred = this.providersById.get(this.preferredProvider);
    if (preferred && eligible.includes(preferred)) {
      // A provider that throws, or that answers with something other than a
      // source, must count as a decline and let the rest of the chain run —
      // it must never abort the whole resolve (see the terminal fall-through
      // in HostedFileProvider.resolve).
      let source = null;
      try {
        source = await preferred.resolve(req);
      } catch {
        source = null;
      }
      if (source && source.status !== PlaybackStatus.UNAVAILABLE) return source;
    }
    const rest = eligible.filter((p) => p !== preferred);
    if (!rest.length) throw new Error(`No playback provider supports ${request.contentType}`);

    // Last-resort providers are consulted ONLY after every other provider has
    // declined. The race below is won by whoever answers FIRST, which is only
    // a fair proxy for "has this title" when every provider does real work
    // before answering. The reserve embeds do not: they build a URL and return
    // RESOLVED immediately, so racing them let them preempt better sources
    // that were still probing — observed live 2026-09-24, where the detail
    // screen reported SuperEmbed AVAILABLE and playback nevertheless resolved
    // to a reserve host, purely because the reserve had answered sooner.
    // Marking them `deferred` puts them where they belong: after everything.
    const immediate = rest.filter((p) => p.deferred !== true);
    const deferred = rest.filter((p) => p.deferred === true);

    // Speed: the remaining providers used to resolve strictly one-by-one, so
    // every movie paid for the slow probes ahead of Vidking (hosted link
    // extraction: 3 sequential upstream fetches; TorBox/Torrent: status
    // round-trips). Race them instead: the first AVAILABLE answer wins — a
    // cheap local "unavailable" answer returns almost immediately and only
    // truly-unavailable chains pay the full probe cost. UNAVAILABLE answers
    // arriving early (all probed) short-circuit the race.
    try {
      if (immediate.length) return await this.race(immediate, req);
    } catch (err) {
      // Every real provider declined — this is what the reserve tier is for.
      if (!deferred.length) throw err;
    }
    if (!deferred.length) throw new Error("No source available for this title");
    return await this.race(deferred, req);
  }

  // Races the given providers: the first AVAILABLE answer wins; if every one
  // declines, the last reported reason is surfaced.
  async race(providers, request) {
    const attempts = providers.map((p) =>
      p.resolve(request).then((source) => ({ provider: p, source })),
    );
    return await new Promise((resolve, reject) => {
      let pending = attempts.length;
      let settled = false;
      attempts.forEach((attempt) => {
        attempt.then(({ source }) => {
          if (settled) return;
          // A missing source counts as a decline (see resolve()); without the
          // guard this threw inside the handler, which left the race pending
          // forever instead of falling through to the reserve tier.
          if (source && source.status !== PlaybackStatus.UNAVAILABLE) {
            settled = true;
            resolve(source);
          } else if (--pending === 0 && !settled) {
            settled = true;
            reject(new Error((source && source.errorMessage) || "No source available for this title"));
          }
        });
        attempt.catch(() => {
          if (--pending === 0 && !settled) {
            settled = true;
            reject(new Error("No source available for this title"));
          }
        });
      });
    });
  }
}